import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiMail,
  FiPhone,
  FiGlobe,
  FiMapPin,
  FiUsers,
  FiBriefcase,
  FiEdit,
  FiArrowLeft,
  FiDatabase,
  FiShield
} from 'react-icons/fi';
import { getCompanyByID,getAllRightsEdit } from '../../services/api/SuperAdminApi';
import { useLoading } from '../../context/LoadingContext';
import './CompanyDetailsPage.scss';
import { BsCurrencyRupee } from "react-icons/bs";



const CompanyDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const [companyData, setCompanyData] = useState(null);
  const [error, setError] = useState(null);
  const [moduleRights, setModuleRights] = useState(null);

  const fetchCompanyData = useCallback(() => {
    if (id) {
      showLoading('Loading company details...');
      setError(null);
      getCompanyByID(id)
        .then((res) => {
          // Store the complete response data (both company and planData)
          setCompanyData(res.data);
          hideLoading();
        })
        .catch((error) => {
          console.error('Error fetching company details:', error);
          setError('Failed to load company details');
          hideLoading();
        });
    }
  }, [id]);


  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  const formatLocation = (company) => {
    const parts = [];
    if (company.address) parts.push(company.address);
    if (company.state) parts.push(company.state);
    if (company.country) parts.push(company.country);
    if (company.zip) parts.push(company.zip);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const getCompanyInitials = (name) => {
    if (!name) return 'C';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatRightName = (rightKey) => {
    // Map specific keys to more user-friendly names
    const keyMappings = {
      'mobileAccess': 'Mobile App Access',
      'mobileAppAccess': 'Mobile App Access',
      'cases': 'Cases',
      'clients': 'Clients',
      'expenses': 'Expenses',
      'invoices': 'Invoices',
      'labels': 'Labels',
      'notes': 'Notes',
      'tasks': 'Tasks',
      'teams': 'Teams',
      'branch': 'Branch'
    };

    return keyMappings[rightKey] || rightKey
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleEditClick = () => {
    navigate(`/home/superadmin/editcompany/${id}`, {
      state: { fromTab: 'companies' }
    });
  };

  const handleBackClick = () => {
    navigate('/home/superadmin', {
      state: { fromTab: 'companies' }
    });
  };

  if (error || !companyData) {
    return (
      <div className="company-detail-container">
        <div className="error-message">{error || 'Company not found'}</div>
      </div>
    );
  }

  return (
    <div className="company-detail-container">
      <div className="company-card">
        {/* Header */}
        <div className="company-header">
          <div className="avatar">
            {getCompanyInitials(companyData?.company?.companyName)}
          </div>
          <div className="company-title">
            <h2>{companyData.company?.companyName}</h2>
            <span className={`company-status ${companyData?.company?.isActive ? 'active' : 'inactive'}`}>
              {companyData.company?.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Company Details Grid */}
        <div className="company-details-grid">
          <div className="detail-item">
            <FiMail className="icon" />
            <div>
              <label>Email</label>
              <p>{companyData.company?.email || 'N/A'}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiPhone className="icon" />
            <div>
              <label>Phone</label>
              <p>{companyData.company?.phno || 'N/A'}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiGlobe className="icon" />
            <div>
              <label>Website</label>
              <p>
                {companyData.company?.website ? (
                  <a href={companyData.company.website} target="_blank" rel="noopener noreferrer">
                    {companyData.company.website}
                  </a>
                ) : 'N/A'}
              </p>
            </div>
          </div>

          <div className="detail-item">
            <FiMapPin className="icon" />
            <div>
              <label>Location</label>
              <p>{formatLocation(companyData.company)}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiUsers className="icon" />
            <div>
              <label>Total Users</label>
              <p>{companyData.company?.users?.length || 0}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiBriefcase className="icon" />
            <div>
              <label>Branches</label>
              <p>{companyData.company?.branches?.length || 0}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiDatabase className="icon" />
            <div>
              <label>Data Scraping</label>
              <p>
                <span className={`role-badge ${companyData.planData?.rights?.scraping ? 'enabled' : 'disabled'}`}>
                  {companyData.planData?.rights?.scraping ? 'Enabled' : 'Disabled'}
                </span>
              </p>
            </div>
          </div>

          <div className="detail-item">
            {/* <BiRupee className="icon" /> */}
            <BsCurrencyRupee className="icon" />
            <div>
              <label>Payment Status</label>
              <p>
                <span className={`payment-status ${companyData.company?.subscription?.paymentStatus?.toLowerCase().replace(' ', '-') || 'unknown'}`}>
                  {companyData.company?.subscription?.paymentStatus || 'N/A'}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Module Permissions Section */}
        {/* {moduleRights && Object.keys(moduleRights).length > 0 && (
            <div className="company-permissions-section">
              <div className="section-header">
                <div className="section-title">
                  <FiShield className="icon" />
                  <h3>Module Rights</h3>
                </div>
              </div>

              <div className="permissions-grid">
                {Object.entries(moduleRights).map(([rightKey, hasPermission]) => (
                  <div
                    key={rightKey}
                    className={`permission-item ${hasPermission ? 'enabled' : 'disabled'}`}
                  >
                    <div className="permission-details">
                      <div className="permission-name">
                        {formatRightName(rightKey)}
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={hasPermission}
                        readOnly 
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )} */}

        {/* Action Buttons */}
        <div className="company-actions">
          <button className="edit-company-btn" onClick={handleEditClick}>
            <FiEdit />
            Edit Company
          </button>
          <button className="back-btn" onClick={handleBackClick}>
            <FiArrowLeft />
            Back to Companies
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailsPage;
